import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listReminderDispatches } from '../../../../api/collections';
import { apiErrorMessage, apiErrorStatus, cooldownByChannel } from '../utils/collectionReminders';

// Historial de la nota: sus recordatorios más recientes (un envío por canal y disparador).
const NOTE_HISTORY_LIMIT = 50;
// Filas recientes del COMERCIO para estimar el enfriamiento de 24 h, que billing aplica por
// comercio y canal (no por nota). Alcanza con holgura para un día de envíos.
const COMPANY_RECENT_LIMIT = 100;
// "Enviar ahora" sale en un ciclo corto del worker, unos segundos después del 202: se vuelve
// a leer el historial pasado ese lapso para mostrar el resultado sin recargar la página.
const REFRESH_AFTER_QUEUE_MS = 4000;

const idOf = (value) => String((typeof value === 'object' && value !== null ? value._id : value) ?? '');

/**
 * Recordatorios de una nota (GET dispatches?noteId=) y enfriamiento vigente de su comercio
 * (GET dispatches?companyId=), para el detalle de la nota.
 *
 * @param {object|null} note nota cargada (con `_id` y `companyId`)
 * @param {{ enabled?: boolean }} options
 * @returns {{ history: object[], loading: boolean,
 *   error: { status: number|null, message: string|null } | null,
 *   cooldown: ReturnType<typeof cooldownByChannel>, reload: () => Promise<void>,
 *   refreshAfterQueue: () => void }}
 */
export default function useNoteReminders(note, { enabled = true } = {}) {
  const noteId = idOf(note?._id);
  const companyId = idOf(note?.companyId);
  const [state, setState] = useState({ history: [], companyRecent: [], loading: false, error: null });
  const requestSeq = useRef(0);
  const refreshTimer = useRef(null);

  const load = useCallback(async () => {
    if (!noteId) return;
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [history, recent] = await Promise.all([
        listReminderDispatches({ noteId, limit: NOTE_HISTORY_LIMIT }),
        companyId
          ? listReminderDispatches({ companyId, limit: COMPANY_RECENT_LIMIT })
          : Promise.resolve({ data: { items: [] } }),
      ]);
      if (seq !== requestSeq.current) return;
      setState({
        history: history.data?.items ?? [],
        companyRecent: recent.data?.items ?? [],
        loading: false,
        error: null,
      });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setState((s) => ({
        ...s,
        loading: false,
        error: { status: apiErrorStatus(err), message: apiErrorMessage(err) },
      }));
    }
  }, [noteId, companyId]);

  useEffect(() => {
    if (enabled) load();
    return () => {
      requestSeq.current += 1;
    };
  }, [enabled, load]);

  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    [],
  );

  const refreshAfterQueue = useCallback(() => {
    load();
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      load();
    }, REFRESH_AFTER_QUEUE_MS);
  }, [load]);

  const cooldown = useMemo(() => cooldownByChannel(state.companyRecent), [state.companyRecent]);

  return {
    history: state.history,
    loading: state.loading,
    error: state.error,
    cooldown,
    reload: load,
    refreshAfterQueue,
  };
}
